import { CustomMenuListCode } from '@/constants/custom-menu-list-code';
import { BuildingMenuType } from '@/enums';
import { useConfigStore } from '@/stores';
import type { GNBItemType } from '@/types';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

type UsePageMenuMetaOptions = {
    menuCode?: string;
    menuType?: BuildingMenuType | null;
};

type PageMenuMeta = {
    title: string;
    description: string;
    menuItem: GNBItemType | undefined;
};

/**
 * Resolves page title/description from config.ui.menu (GNB source of truth).
 * Priority: route gnbMenuId → menuCode → menuType.
 */
export function usePageMenuMeta({ menuCode, menuType }: UsePageMenuMetaOptions = {}): PageMenuMeta {
    const menu = useConfigStore((state) => state.config?.ui?.menu);
    const { gnbMenuId } = useParams<{ gnbMenuId?: string }>();

    return useMemo(() => {
        const items = menu ?? [];

        const matched =
            (gnbMenuId ? items.find((item) => item.id === gnbMenuId) : undefined) ??
            (menuCode ? items.find((item) => item.menuCode === menuCode) : undefined) ??
            (menuType ? items.find((item) => item.type === menuType) : undefined);

        return {
            title: matched?.label ?? '',
            description: matched?.dataDesc ?? '',
            menuItem: matched,
        };
    }, [menu, gnbMenuId, menuCode, menuType]);
}

/** Convenience hook for Event page (menuCode = EVENT). */
export function useEventPageMenuMeta(): PageMenuMeta {
    return usePageMenuMeta({
        menuCode: CustomMenuListCode.EVENT,
        menuType: BuildingMenuType.EVENT,
    });
}
